import {
  isAccountFacebookPage,
  isInstagramAccount,
} from "@/entities/accounts/libs/account-platform";
import { User } from "features/user";
import type { AccountType } from "shared/types/accounts";
import { hasEntitlement } from "utils/has-entitlement";
import { isReverseTrial } from "utils/reverse-trial";

type AdsPermissionUser =
  | (Partial<Pick<User, "plan">> & {
      [key: string]: unknown;
      role?: string;
      subscription?: {
        subscription?: {
          status?: string;
        };
      };
      preferences?: {
        [key: string]: unknown;
        canBoostPosts?: boolean;
        entitlements?: Record<string, string | undefined>;
      };
      canBoostPosts?: boolean;
    })
  | null
  | undefined;

export function isAdsFeatureOn(account: AccountType, user: AdsPermissionUser) {
  if (!isAccountFacebookPage(account)) return false;
  // Reverse trial → every feature on.
  if (isReverseTrial(user)) return true;
  // Entitlement-first: if the backend grants the meta-ads entitlement the
  // feature is on regardless of plan code name.
  if (hasEntitlement(user, "meta-ads")) return true;
  // Legacy plan-code fallback for non-migrated users.
  return !!user?.plan?.includes("sked-enterprise");
}

export function isAdsPermissionRequired(
  account: AccountType,
  user: AdsPermissionUser
) {
  const scope = "ads_read";
  // A user-token permission can be targeted to an ad account and therefore be
  // absent from the historical Page/Instagram-scoped `session.scopes` list.
  // Prefer the persisted capability result when present, then fall back to
  // the old scope list for accounts that have not been revalidated yet.
  const hasReportingCapability = account.metaAdsCapabilities
    ? account.metaAdsCapabilities.reporting
    : account.session?.grantedScopes?.includes(scope) ||
      account.session?.scopes?.includes(scope);
  // SKE-4375: reconnection is needed when the ads_read scope is missing OR when
  // Meta ad-account discovery flagged this account's token as unable to
  // enumerate ad accounts. The latter is an ADS-only signal — it never touches
  // Insights/Inbox (which key off `promptApiReconnect`).
  return (
    isAdsFeatureOn(account, user) &&
    (!hasReportingCapability || !!account.metaAdsReconnectRequired)
  );
}

export function getRequiredAdsPermissionAccounts(
  accounts: AccountType[] | undefined,
  accountsToUpdate:
    { facebook?: AccountType[]; instagram?: AccountType[] } | undefined,
  user: AdsPermissionUser
) {
  return (accounts || []).filter((acc) => {
    if (accountsToUpdate?.facebook?.some((a) => a._id === acc._id)) {
      return false;
    }
    return isAdsPermissionRequired(acc, user);
  });
}

/**
 * Whether a user on a given plan can access collaboration. Kept for callers
 * that only have the plan string on hand (legacy callers). Prefer
 * `hasTeamwork` from `utils/feature-gates` which also considers entitlements
 * and reverse-trial state.
 */
export function isCollaborationEnabledForPlan(plan: string) {
  return [
    "sked-professional",
    "sked-enterprise",
    "sked-custom",
    "sked-essentials",
  ].some((c) => (plan || "").includes(c));
}

export function canBoostPost(
  account: AccountType,
  user: AdsPermissionUser
): boolean {
  const isMeta = isAccountFacebookPage(account) || isInstagramAccount(account);
  // Collaborator boost access is primarily persisted in preferences, but some
  // payloads may still expose the legacy flattened field.
  const hasPermission =
    user?.role !== "collaborator" ||
    user.preferences?.canBoostPosts === true ||
    user?.canBoostPosts === true;
  return isMeta && hasPermission;
}

/**
 * Capability state is separate from entitlement/collaborator permission. A
 * Meta account may be perfectly healthy for ads reporting while lacking the
 * user-token permissions required to create campaigns.
 *
 * `undefined` means this account predates capability persistence. Keep that
 * state visible so the server can perform the authoritative preflight.
 */
export function hasAdsBoostingCapability(account: AccountType): boolean | null {
  if (!account.metaAdsCapabilities) {
    return null;
  }
  return account.metaAdsCapabilities.boosting;
}

export function isAdsBoostingUnavailable(account: AccountType): boolean {
  return hasAdsBoostingCapability(account) === false;
}
